import React, { FC } from 'react';
import { ProductPageQuery } from '../../@types/codegen/graphql';
import { useTranslation } from 'next-i18next';
import { ProductPageLink, useProductLink } from './product-page-link';
import { readableAddress } from '../../helper/readable-address';
import { useRouter } from 'next/router';
import {
  ShortInfoRentalStationSelect,
  ProductPrice,
  ShortInfoAction,
  ShortInfo, ProductBenefits,
} from '@digando/common-ui';
import { useRecoilValue } from 'recoil';
import { lastSelectedProjectIdState } from '../../store/recoil/product-configurator';
import { ServiceFeeTitle } from '../../helper/service-fee-title-by-key';
import { CardLoginHint } from './card/card-login-hint';

interface IShortInfoProps {
  productBundle: ProductPageQuery['productBundle'];
  productPrice: ProductPageQuery['productPrice'];
  usps: string[];
}

export const ShortInfoProductDetail: FC<IShortInfoProps> = ({
  productBundle,
  productPrice,
  usps,
}) => {
  const [t] = useTranslation(['product-page', 'checkout-page']);
  const bundle = productBundle?.bundle;

  const { getProductLinkParams } = useProductLink();
  const isDeliverable = !!productBundle?.bundle?.availableRentalStations.find((rentalStation) => true === rentalStation.shippingOptions.deliveryEnabled);
  const router = useRouter();
  const rentalStationId = router.query.rentalStationId as string | undefined;
  const lastSelectedProjectId = useRecoilValue(lastSelectedProjectIdState);

  const options = productBundle?.bundle?.availableRentalStations.map((rentalStation) => ({
    key: rentalStation.id,
    value: rentalStation.id,
    name: rentalStation.name,
    description: readableAddress(rentalStation),
  })) ?? [];

  const minRentalDuration = productBundle?.bundle?.selectedRentalStation?.minimumRentalPeriodDays ?? 1;

  const onRentalStationChanged = (id: string) => {
    const rentalStation = productBundle?.bundle?.availableRentalStations.find((rentalStation) => rentalStation.id === id);

    router.replace(getProductLinkParams({
      href: location.pathname,
      rentalStationId: id,
      shippingOptions: rentalStation?.shippingOptions,
    }), undefined, { scroll: false });
  };

  if (!bundle) {
    return null;
  }

  return (
    <>
      <ShortInfo>
        <ShortInfoRentalStationSelect
          rentalStationsOptions={options}
          onRentalStationChanged={onRentalStationChanged}
          selectedRentalStationId={rentalStationId}
        />
        <ProductPrice
          productPrice={{
            productPrice: productPrice?.productPrice,
            productPriceError: productPrice?.productPriceError,
          }}
          selectedRentalStationId={rentalStationId}
          hasHiddenPrices={bundle.selectedProduct?.hasHiddenPrices ?? false}
          serviceFees={productBundle?.bundle?.priceInformation?.serviceFees?.map(serviceFee => {
            return {
              key: serviceFee.key,
              value: serviceFee.value,
              title: <ServiceFeeTitle key={serviceFee.key} feeType={serviceFee.key} />,
            };
          }) ?? []}
          loginHint={<CardLoginHint />}
          translations={{
            serviceFeeTitle: t('product-page:short-info.service-fee-title'),
            minRentalDuration: t('product-page:short-info.minimum-rental-duration', { amount: minRentalDuration }),
            pricePerDay: t('common:day'),
            reducedPricePrefix: t('product-page:short-info.reduced-price-prefix'),
            calculationError: t('checkout-page:price-calculation-error'),
          }}
        />
        <ShortInfoAction
          actionTitle={t('product-page:short-info.short-info-button-title')}
          linkWrapper={(children) => (
            <ProductPageLink
              href={bundle.type.configuratorPageUrl}
              rentalStationId={rentalStationId}
              deliveryAddressId={lastSelectedProjectId}
              shippingOptions={bundle?.selectedRentalStation?.shippingOptions}
            >
              {children}
            </ProductPageLink>
          )}
        />
      </ShortInfo>

      <ProductBenefits
        benefits={usps}
        availableProducts={bundle?.availableProducts ?? 0}
        availabilityText={2 <= (bundle?.availableProducts ?? 0) ? t('product-page:short-info.sufficient') : t('product-page:short-info.available')}
        isDeliverable={isDeliverable}
        deliveryText={t('product-page:short-info.delivery-possible')}
      />
    </>
  );
};
